// =====================================================================
// crmCustomer — Pilots: list tab + its three dialogs (New / Edit /
// Checklist / Log). All shared constants and helpers come from
// pilots-shared.jsx via the global scope.
// =====================================================================

// ── New pilot dialog ────────────────────────────────────────────────
function NewPilotDialog({ onClose, defaultAccountId }) {
  const accounts = D.ACCOUNTS || [];
  const users = assignableReps();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    accountId: defaultAccountId || accounts[0]?.id || "",
    name: "",
    status: "Qualifying",
    startDate: today,
    pilotFeePounds: 3000,
    annualStarterPounds: 12000,
    ownerId: users[0]?.id || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // A client can run up to three parallel pilots. Surface a soft warning
  // when adding the fourth so the user can still proceed if needed (e.g.
  // a stopped pilot is still on the books for reporting).
  const existing = (D.PILOTS || []).filter(p => p.accountId === form.accountId);
  const overCap = existing.length >= 3;

  async function handleSave() {
    if (!form.accountId) { setError("Pick an account."); return; }
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    try {
      const start = form.startDate || null;
      const day90 = start ? addDaysIso(start, 90) : null;
      const day180 = start ? addDaysIso(start, 180) : null;
      const pilotId = "pilot_" + Math.random().toString(36).slice(2, 10);
      await tpAPI.pilots.create({
        id: pilotId,
        accountId: form.accountId,
        name: form.name.trim(),
        status: form.status,
        startDate: start,
        day90Date: day90,
        day180Date: day180,
        ownerId: form.ownerId || null,
        pilotFeePence: Math.round((Number(form.pilotFeePounds) || 0) * 100),
        annualStarterPence: Math.round((Number(form.annualStarterPounds) || 0) * 100),
      });
      // Seed the first gate (Month 1 review) so the pilot lands in the
      // Gatelog with a pending entry from day zero. Baseline maturity is
      // 1 by convention. createNextGateSafely swallows the UNIQUE-index
      // conflict that would fire if a sibling tab already seeded it.
      const firstGate = PILOT_GATES[0];
      await createNextGateSafely({
        id: "gate_" + Math.random().toString(36).slice(2, 10),
        pilotId,
        gate: firstGate.key,
        gateDate: start ? addDaysIso(start, firstGate.dayOffset) : null,
        maturityScore: 1,
        decision: "pending",
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to create pilot.");
      setSaving(false);
    }
  }

  return (
    <Dialog title="New pilot" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Creating…" : "Create pilot"}</button>
    </>}>
      <div className="form-field"><label>Account *</label>
        <select value={form.accountId} onChange={e => set("accountId", e.target.value)}>
          <option value="">— Select account —</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      {overCap && <div className="muted" style={{ fontSize: 12, marginTop: -6, marginBottom: 8 }}>
        This account already has {existing.length} pilots. Three concurrent is the realistic ceiling — proceed only if one is stopped.
      </div>}
      <div className="form-field"><label>Pilot name *</label>
        <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Project Alpha — security pillar" autoFocus />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)}>
            {PILOT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Owner</label>
          <select value={form.ownerId} onChange={e => set("ownerId", e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Start date (drives day 90 / day 180)</label>
          <input type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} />
        </div>
        <div className="form-field"><label>Pilot fee (£)</label>
          <input type="number" min="0" value={form.pilotFeePounds} onChange={e => set("pilotFeePounds", e.target.value)} />
        </div>
        <div className="form-field"><label>Annual starter (£)</label>
          <input type="number" min="0" value={form.annualStarterPounds} onChange={e => set("annualStarterPounds", e.target.value)} />
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// ── Edit pilot dialog — quick details, scores, decision ─────────────
function EditPilotDialog({ pilot, onClose }) {
  const accounts = D.ACCOUNTS || [];
  const users = assignableReps(null, pilot.ownerId);
  const contacts = (D.CONTACTS || []).filter(c => c.accountId === pilot.accountId);
  const [form, setForm] = useState({
    name: pilot.name || "",
    status: pilot.status || "Qualifying",
    ownerId: pilot.ownerId || "",
    championContactId: pilot.championContactId || "",
    economicBuyerContactId: pilot.economicBuyerContactId || "",
    startDate: pilot.startDate || "",
    day90Date: pilot.day90Date || "",
    day180Date: pilot.day180Date || "",
    baselineDate: pilot.baselineDate || "",
    baselineScore: pilot.baselineScore ?? "",
    closeDate: pilot.closeDate || "",
    closeScore: pilot.closeScore ?? "",
    maturityPillar: pilot.maturityPillar || "",
    pilotFeePounds: poundsFromPence(pilot.pilotFeePence) ?? 3000,
    annualStarterPounds: poundsFromPence(pilot.annualStarterPence) ?? 12000,
    paid: !!pilot.paid,
    paidDate: pilot.paidDate || "",
    successCriteria: pilot.successCriteria || "",
    timeSavedHours: pilot.timeSavedHours ?? "",
    costSavedPounds: poundsFromPence(pilot.costSavedPence) ?? "",
    decisionAt90: pilot.decisionAt90 || "",
    decisionDate: pilot.decisionDate || "",
    annualContractValuePounds: poundsFromPence(pilot.annualContractValuePence) ?? "",
    feeCredited: !!pilot.feeCredited,
    expansionProjects: pilot.expansionProjects ?? "",
    referenceConsent: !!pilot.referenceConsent,
    referenceQuote: pilot.referenceQuote || "",
    source: pilot.source || "",
    findingsSurfaced: pilot.findingsSurfaced ?? "",
    findingsActioned: pilot.findingsActioned ?? "",
    notes: pilot.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // Keep `status` aligned with `decisionAt90` so the list, metrics, and
  // filter all see the same end-state. The user can still override the
  // status afterwards (this only fires on the decision change itself).
  // Only nudges when the existing status isn't already a terminal one,
  // so we don't bounce someone who manually set "Stopped" earlier.
  function setDecisionAt90(value) {
    setForm(f => {
      const inferredStatus = value === "convert" ? "Converted"
                           : value === "stop"    ? "Stopped"
                           : value === "extend"  ? "Extension"
                                                 : null;
      const terminal = new Set(["Converted", "Stopped", "Extension"]);
      const shouldNudgeStatus = inferredStatus && !terminal.has(f.status);
      return {
        ...f,
        decisionAt90: value,
        ...(shouldNudgeStatus ? { status: inferredStatus } : {}),
      };
    });
  }

  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    try {
      const num = (v) => v === "" || v == null ? null : Number(v);
      await tpAPI.pilots.update(pilot.id, {
        name: form.name.trim(),
        status: form.status,
        ownerId: form.ownerId || null,
        championContactId: form.championContactId || null,
        economicBuyerContactId: form.economicBuyerContactId || null,
        startDate: form.startDate || null,
        day90Date: form.day90Date || null,
        day180Date: form.day180Date || null,
        baselineDate: form.baselineDate || null,
        baselineScore: num(form.baselineScore),
        closeDate: form.closeDate || null,
        closeScore: num(form.closeScore),
        maturityPillar: form.maturityPillar || null,
        pilotFeePence: form.pilotFeePounds === "" ? null : Math.round(Number(form.pilotFeePounds) * 100),
        annualStarterPence: form.annualStarterPounds === "" ? null : Math.round(Number(form.annualStarterPounds) * 100),
        paid: form.paid,
        paidDate: form.paidDate || null,
        successCriteria: form.successCriteria || null,
        timeSavedHours: num(form.timeSavedHours),
        costSavedPence: form.costSavedPounds === "" ? null : Math.round(Number(form.costSavedPounds) * 100),
        decisionAt90: form.decisionAt90 || null,
        decisionDate: form.decisionDate || null,
        annualContractValuePence: form.annualContractValuePounds === "" ? null : Math.round(Number(form.annualContractValuePounds) * 100),
        feeCredited: form.feeCredited,
        expansionProjects: num(form.expansionProjects),
        referenceConsent: form.referenceConsent,
        referenceQuote: form.referenceQuote || null,
        source: form.source || null,
        findingsSurfaced: num(form.findingsSurfaced),
        findingsActioned: num(form.findingsActioned),
        notes: form.notes || null,
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save pilot.");
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true); setError(null);
    try {
      await tpAPI.pilots.remove(pilot.id);
      onClose();
    } catch (e) {
      setError(e.message || "Failed to delete pilot.");
      setSaving(false);
    }
  }

  const acct = accounts.find(a => a.id === pilot.accountId);
  const delta = (form.closeScore !== "" && form.baselineScore !== "")
    ? Number(form.closeScore) - Number(form.baselineScore) : null;

  return (
    <Dialog title={`Edit pilot · ${acct?.name || pilot.accountId}`} onClose={onClose}
      footer={<div style={{ display: "flex", width: "100%", gap: 8 }}>
        {confirmDel ? (
          <>
            <span className="muted" style={{ fontSize: 12, alignSelf: "center", marginRight: "auto" }}>Delete this pilot and all its log entries?</span>
            <button className="btn btn-sm" onClick={() => setConfirmDel(false)}>Cancel</button>
            <button className="btn btn-sm" style={{ background: "var(--red, #b91c1c)", color: "#fff" }} onClick={handleDelete} disabled={saving}>{saving ? "Deleting…" : "Delete"}</button>
          </>
        ) : (
          <>
            <button className="btn btn-sm" style={{ marginRight: "auto" }} onClick={() => setConfirmDel(true)}>Delete</button>
            <button className="btn btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          </>
        )}
      </div>}>
      <div className="form-field"><label>Pilot name *</label>
        <input value={form.name} onChange={e => set("name", e.target.value)} />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)}>
            {PILOT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Owner</label>
          <select value={form.ownerId} onChange={e => set("ownerId", e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Source</label>
          <select value={form.source} onChange={e => set("source", e.target.value)}>
            <option value="">— Unknown —</option>
            {PILOT_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Champion (contact)</label>
          <select value={form.championContactId} onChange={e => set("championContactId", e.target.value)}>
            <option value="">— None —</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Economic buyer (contact)</label>
          <select value={form.economicBuyerContactId} onChange={e => set("economicBuyerContactId", e.target.value)}>
            <option value="">— None —</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Start</label>
          <input type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} />
        </div>
        <div className="form-field"><label>Day 90</label>
          <input type="date" value={form.day90Date} onChange={e => set("day90Date", e.target.value)} />
        </div>
        <div className="form-field"><label>Day 180</label>
          <input type="date" value={form.day180Date} onChange={e => set("day180Date", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Pilot fee (£)</label>
          <input type="number" min="0" value={form.pilotFeePounds} onChange={e => set("pilotFeePounds", e.target.value)} />
        </div>
        <div className="form-field"><label>Annual starter (£)</label>
          <input type="number" min="0" value={form.annualStarterPounds} onChange={e => set("annualStarterPounds", e.target.value)} />
        </div>
        <div className="form-field"><label>Paid?</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, paddingTop: 6 }}>
            <input type="checkbox" checked={form.paid} onChange={e => set("paid", e.target.checked)} />
            <span className="muted" style={{ fontSize: 12 }}>£3k collected before kickoff</span>
          </label>
        </div>
        <div className="form-field"><label>Paid date</label>
          <input type="date" value={form.paidDate} onChange={e => set("paidDate", e.target.value)} disabled={!form.paid} />
        </div>
      </div>
      <div className="form-field"><label>Agreed success criteria (in writing)</label>
        <textarea rows="2" value={form.successCriteria} onChange={e => set("successCriteria", e.target.value)} placeholder="What does success look like to the client?" />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Baseline score (1–5)</label>
          <input type="number" min="1" max="5" value={form.baselineScore} onChange={e => set("baselineScore", e.target.value)} />
        </div>
        <div className="form-field"><label>Baseline date</label>
          <input type="date" value={form.baselineDate} onChange={e => set("baselineDate", e.target.value)} />
        </div>
        <div className="form-field"><label>Close score (1–5)</label>
          <input type="number" min="1" max="5" value={form.closeScore} onChange={e => set("closeScore", e.target.value)} />
        </div>
        <div className="form-field"><label>Close date</label>
          <input type="date" value={form.closeDate} onChange={e => set("closeDate", e.target.value)} />
        </div>
      </div>
      {delta !== null && (
        <div className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 8 }}>
          Maturity delta: <strong style={{ color: delta > 0 ? "var(--green, #047857)" : delta < 0 ? "var(--red, #b91c1c)" : "var(--text-muted)" }}>{delta > 0 ? "+" : ""}{delta}</strong>
        </div>
      )}
      <div className="form-row">
        <div className="form-field"><label>Launch pillar</label>
          <input value={form.maturityPillar} onChange={e => set("maturityPillar", e.target.value)} placeholder="e.g. Security" />
        </div>
        <div className="form-field"><label>Time saved (hrs)</label>
          <input type="number" min="0" step="0.5" value={form.timeSavedHours} onChange={e => set("timeSavedHours", e.target.value)} />
        </div>
        <div className="form-field"><label>Cost saved (£)</label>
          <input type="number" min="0" value={form.costSavedPounds} onChange={e => set("costSavedPounds", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Findings surfaced</label>
          <input type="number" min="0" value={form.findingsSurfaced} onChange={e => set("findingsSurfaced", e.target.value)} placeholder="threat-model findings, gaps, weaknesses" />
        </div>
        <div className="form-field"><label>Findings actioned</label>
          <input type="number" min="0" value={form.findingsActioned} onChange={e => set("findingsActioned", e.target.value)} placeholder="of those, how many the team closed" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Day-90 decision</label>
          <select value={form.decisionAt90} onChange={e => setDecisionAt90(e.target.value)}>
            <option value="">— Pending —</option>
            <option value="convert">Convert</option>
            <option value="extend">Extend</option>
            <option value="stop">Stop</option>
          </select>
        </div>
        <div className="form-field"><label>Decision date</label>
          <input type="date" value={form.decisionDate} onChange={e => set("decisionDate", e.target.value)} />
        </div>
        <div className="form-field"><label>Annual contract value (£)</label>
          <input type="number" min="0" value={form.annualContractValuePounds} onChange={e => set("annualContractValuePounds", e.target.value)} />
        </div>
        <div className="form-field"><label>Pilot fee credited?</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, paddingTop: 6 }}>
            <input type="checkbox" checked={form.feeCredited} onChange={e => set("feeCredited", e.target.checked)} />
          </label>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Expansion to projects</label>
          <input type="number" min="0" max="20" value={form.expansionProjects} onChange={e => set("expansionProjects", e.target.value)} placeholder="e.g. 5" />
        </div>
        <div className="form-field"><label>Reference consent</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, paddingTop: 6 }}>
            <input type="checkbox" checked={form.referenceConsent} onChange={e => set("referenceConsent", e.target.checked)} />
          </label>
        </div>
      </div>
      <div className="form-field"><label>Verbatim reference quote</label>
        <textarea rows="2" value={form.referenceQuote} onChange={e => set("referenceQuote", e.target.value)} placeholder="Captured at day 90 while goodwill is highest." />
      </div>
      <div className="form-field"><label>Notes</label>
        <textarea rows="2" value={form.notes} onChange={e => set("notes", e.target.value)} />
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// ── Onboarding checklist modal — persisted per pilot ────────────────
function ChecklistDialog({ pilot, onClose }) {
  // Local mirror of pilot.checklistState. Toggling updates locally for
  // snappy feedback, then persists. We do not block toggling on the
  // round-trip — a save failure rolls the offending key back.
  const [state, setState] = useState(pilot.checklistState || {});
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  async function toggle(key) {
    const prev = !!state[key];
    const next = { ...state, [key]: !prev };
    setState(next);
    setSavingKey(key);
    setError(null);
    try {
      await tpAPI.pilots.update(pilot.id, { checklistState: next });
    } catch (e) {
      // Roll back so the UI matches what's persisted.
      setState(s => ({ ...s, [key]: prev }));
      setError(e.message || "Failed to save.");
    } finally {
      setSavingKey(null);
    }
  }

  const { done, total } = checklistProgress(state);
  const pct = total ? Math.round(done / total * 100) : 0;

  return (
    <Dialog title={`Onboarding checklist · ${pilot.name}`} onClose={onClose}
      // The checklist has ~45 task rows of multi-line text. The default
      // 520px width chops most tasks to 3-4 lines and hurts scannability;
      // a 66vw-but-capped width gives the rows room to breathe without
      // running edge-to-edge on wide monitors.
      width="min(960px, 66vw)"
      footer={<>
        <span className="muted" style={{ fontSize: 12, marginRight: "auto" }}>{done} / {total} ticked · {pct}%</span>
        <button className="btn btn-sm" onClick={onClose}>Close</button>
      </>}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ height: 6, background: "var(--bg-sunken, #f4f1ea)", borderRadius: 999 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--green, #047857)", borderRadius: 999, transition: "width .25s" }} />
        </div>
      </div>
      {error && <div className="form-error" style={{ marginBottom: 8 }}>{error}</div>}
      {PILOT_CHECKLIST.map((ph, pi) => {
        const pTotal = ph.tasks.length;
        const pDone = ph.tasks.filter((_, ti) => state[`p${pi}-${ti}`]).length;
        return (
          <div key={pi} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>{ph.tag}</span>
              <strong style={{ fontSize: 14 }}>{ph.title}</strong>
              <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>{pDone} / {pTotal}</span>
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>{ph.sub}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {ph.tasks.map((tk, ti) => {
                const key = `p${pi}-${ti}`;
                const checked = !!state[key];
                const busy = savingKey === key;
                return (
                  <label key={ti} style={{
                    display: "grid", gridTemplateColumns: "20px 1fr", gap: 10,
                    padding: "8px 10px", borderRadius: 4, cursor: "pointer",
                    background: checked ? "var(--bg-sunken, #f4f1ea)" : "transparent",
                    border: "1px solid var(--divider, #e7e2d5)",
                    opacity: busy ? 0.7 : 1,
                  }}>
                    <input type="checkbox" checked={checked} disabled={busy} onChange={() => toggle(key)} style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 13, lineHeight: 1.4, textDecoration: checked ? "line-through" : "none", color: checked ? "var(--text-muted)" : "inherit" }}>
                        {tk.t}
                        {tk.c && <Badge tone="red">CRITICAL</Badge>}
                      </div>
                      {tk.n && <div className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 3 }}>{tk.n}</div>}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </Dialog>
  );
}

// ── Read-only details dialog ────────────────────────────────────────
// A focused digest of the pilot's commercial + value picture. Reads
// industry off the linked account; contact name+title off the linked
// contact records; computes maturity delta and uplift % from baseline /
// close scores. Edit button hands off to EditPilotDialog where any of
// these fields can be changed.
function PilotDetailsDialog({ pilot, onClose }) {
  const acct = getAccount(pilot.accountId);
  const champion = getContact(pilot.championContactId);
  const buyer    = getContact(pilot.economicBuyerContactId);

  function contactLine(c) {
    if (!c) return <span className="muted">—</span>;
    const name = `${c.first || ""} ${c.last || ""}`.trim() || "(unnamed)";
    const role = c.title ? ` · ${c.title}` : "";
    return <span>{name}<span className="muted">{role}</span></span>;
  }

  const baseline = pilot.baselineScore;
  const current  = pilot.closeScore;
  const haveBoth = baseline != null && current != null;
  const delta    = haveBoth ? current - baseline : null;
  const upliftPct = (haveBoth && baseline > 0) ? Math.round((delta / baseline) * 100) : null;
  const deltaColor = delta == null ? "var(--text-muted)"
                   : delta > 0     ? "var(--green, #047857)"
                   : delta < 0     ? "var(--red, #b91c1c)"
                                   : "var(--text-muted)";

  const findingsSurfaced = pilot.findingsSurfaced;
  const findingsActioned = pilot.findingsActioned;
  const hasFindings = findingsSurfaced != null || findingsActioned != null;

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <div className="kv" style={{ rowGap: 8 }}>{children}</div>
    </div>
  );

  return (
    <Dialog title={`Pilot details · ${pilot.name}`} onClose={onClose}
      width="min(720px, 60vw)"
      footer={<button className="btn btn-sm" onClick={onClose}>Close</button>}>

      <Section title="Commercial">
        <div className="kv-label">Account</div>
        <div className="kv-value">{acct?.name || <span className="muted">—</span>}</div>
        <div className="kv-label">Industry</div>
        <div className="kv-value">{acct?.industry || <span className="muted">—</span>}</div>
        <div className="kv-label">Source</div>
        <div className="kv-value">{pilot.source || <span className="muted">—</span>}</div>
        <div className="kv-label">ACV</div>
        <div className="kv-value numeric">{fmtPounds(pilot.annualContractValuePence)}</div>
      </Section>

      <Section title="Stakeholders">
        <div className="kv-label">Economic buyer</div>
        <div className="kv-value">{contactLine(buyer)}</div>
        <div className="kv-label">Champion</div>
        <div className="kv-value">{contactLine(champion)}</div>
      </Section>

      <Section title="Maturity">
        <div className="kv-label">Baseline (1–5)</div>
        <div className="kv-value numeric">{baseline ?? <span className="muted">—</span>}</div>
        <div className="kv-label">Current (1–5)</div>
        <div className="kv-value numeric">{current ?? <span className="muted">—</span>}</div>
        <div className="kv-label">Uplift Δ</div>
        <div className="kv-value numeric" style={{ color: deltaColor, fontWeight: 600 }}>
          {delta == null ? "—" : (delta > 0 ? `+${delta}` : `${delta}`)}
        </div>
        <div className="kv-label">Uplift %</div>
        <div className="kv-value numeric" style={{ color: deltaColor, fontWeight: 600 }}>
          {upliftPct == null ? "—" : `${upliftPct > 0 ? "+" : ""}${upliftPct}%`}
        </div>
      </Section>

      <Section title="Outcomes">
        <div className="kv-label">Time saved</div>
        <div className="kv-value">
          {pilot.timeSavedHours != null
            ? <><span className="numeric">{pilot.timeSavedHours.toLocaleString()}</span> hrs <span className="muted">(attested)</span></>
            : <span className="muted">—</span>}
        </div>
        <div className="kv-label">Findings actioned / surfaced</div>
        <div className="kv-value">
          {hasFindings
            ? <><span className="numeric">{findingsActioned ?? 0}</span> / <span className="numeric">{findingsSurfaced ?? 0}</span></>
            : <span className="muted">—</span>}
        </div>
      </Section>
    </Dialog>
  );
}

// Fields auto-seeded from the pilot/account the instant the case study
// opens — the stable "At a glance" metadata the list row already knows.
// The richer results / quote / consent suggestions stay behind the
// explicit "Pull from pilot" button (they're captured later in the run).
const CASE_STUDY_AUTOFILL_KEYS =
  (PILOT_CASE_STUDY_SECTIONS.find(s => s.key === "glance") || { fields: [] })
    .fields.map(f => f.key);

// Everything derivable from a pilot and its linked account/contacts,
// keyed by case-study field. Empty string when nothing is derivable.
function buildPilotSuggestions(pilot) {
  const acct     = getAccount(pilot.accountId);
  const champion = getContact(pilot.championContactId);
  const buyer    = getContact(pilot.economicBuyerContactId);
  const b = pilot.baselineScore, c = pilot.closeScore;
  const delta = (b != null && c != null) ? c - b : null;
  const ref = champion || buyer;
  const attribution = ref
    ? [`${ref.first || ""} ${ref.last || ""}`.trim(), ref.title, acct?.name].filter(Boolean).join(", ")
    : "";
  const timeCost = pilot.timeSavedHours != null
    ? `${pilot.timeSavedHours} hrs saved`
    : (pilot.costSavedPence != null ? `${fmtPounds(pilot.costSavedPence)} saved` : "");
  const findings = (pilot.findingsActioned != null || pilot.findingsSurfaced != null)
    ? `${pilot.findingsActioned ?? 0} of ${pilot.findingsSurfaced ?? 0} actioned`
    : "";
  return {
    client:           acct?.name || "",
    sector:           acct?.industry || "",
    size:             acct?.headcount || "",
    windowStart:      pilot.startDate || "",
    windowEnd:        pilot.day90Date || "",
    baselineMaturity: b != null ? String(b) : "",
    maturityUplift:   delta != null ? (delta > 0 ? `+${delta}` : `${delta}`) : "",
    timeCostSaved:    timeCost,
    findings:         findings,
    quote:            pilot.referenceQuote || "",
    quoteAttribution: attribution,
    consentApproved:  pilot.referenceConsent ? "Yes" : "",
  };
}

// Copy `suggestions` into `base`, filling only blank fields (optionally
// restricted to `keys`). Never overwrites a value the user already has.
function fillBlanksFrom(base, suggestions, keys) {
  const next = { ...base };
  (keys || Object.keys(suggestions)).forEach(k => {
    const v = suggestions[k];
    if (v != null && v !== "" && (next[k] == null || String(next[k]).trim() === "")) next[k] = v;
  });
  return next;
}

// ── Case study capture dialog — persisted per pilot as a JSON blob ──
// The in-app "web page" the user opens from the 📝 row icon. Renders
// PILOT_CASE_STUDY_SECTIONS (from pilots-shared.jsx, mirroring
// docs/case-study-template.md) declaratively, and saves the filled
// values to pilots.caseStudy via the standard pilots update — no
// separate resource. Built to be filled incrementally over the pilot:
// baseline at kickoff, results at day 90, the quote at close. Save
// keeps the dialog open (with a "saved" marker) so the user can keep
// editing across visits.
function CaseStudyDialog({ pilot, onClose }) {
  // Auto-seed the "At a glance" metadata from the row the moment the
  // page opens, so the user never re-types what the CRM already knows.
  // Only blank fields are filled; nothing persists until Save.
  const [form, setForm] = useState(() =>
    fillBlanksFrom(pilot.caseStudy || {}, buildPilotSuggestions(pilot), CASE_STUDY_AUTOFILL_KEYS));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [copied, setCopied] = useState(false);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); setSavedAt(null); }

  const { filled, total } = caseStudyProgress(form);
  const pct = total ? Math.round(filled / total * 100) : 0;

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      const payload = { ...form, updatedAt: new Date().toISOString() };
      await tpAPI.pilots.update(pilot.id, { caseStudy: payload });
      setForm(payload);
      setSavedAt(payload.updatedAt);
    } catch (e) {
      setError(e.message || "Failed to save case study.");
    } finally {
      setSaving(false);
    }
  }

  // Pull the full set of derivable values (results, quote, consent —
  // captured later in the run) into blank fields. Same blanks-only rule
  // as the auto-seed on open, so it never overwrites typed text.
  function pullFromPilot() {
    setForm(f => fillBlanksFrom(f, buildPilotSuggestions(pilot)));
    setSavedAt(null);
  }

  // Render the filled template as markdown — the case study doubles as a
  // sales asset, so a one-click copy of the document is more useful than
  // forcing a re-type elsewhere. Unfilled fields show as a placeholder.
  function toMarkdown() {
    const lines = [`# Case study: ${(form.client || "").trim() || pilot.name || "Pilot"}`, ""];
    PILOT_CASE_STUDY_SECTIONS.forEach(sec => {
      lines.push(`## ${sec.title}`);
      sec.fields.forEach(f => {
        const v = form[f.key];
        const val = (v != null && String(v).trim() !== "") ? String(v).trim() : "_(not captured)_";
        lines.push(`- **${f.label}:** ${val}`);
      });
      lines.push("");
    });
    return lines.join("\n");
  }
  async function copyMarkdown() {
    try {
      await navigator.clipboard.writeText(toMarkdown());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      setError("Clipboard copy failed — your browser may be blocking it.");
    }
  }

  function renderField(f) {
    const val = form[f.key] ?? "";
    if (f.type === "textarea") {
      return <textarea rows={f.rows || 3} value={val} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder || ""} />;
    }
    if (f.type === "select") {
      return (
        <select value={val} onChange={e => set(f.key, e.target.value)}>
          {f.options.map(o => <option key={o} value={o}>{o === "" ? "— Select —" : o}</option>)}
        </select>
      );
    }
    return <input type={f.type === "date" ? "date" : "text"} value={val} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder || ""} />;
  }

  return (
    <Dialog title={`Case study · ${pilot.name}`} onClose={onClose}
      width="min(840px, 70vw)"
      footer={<div style={{ display: "flex", width: "100%", gap: 8, alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 12, marginRight: "auto" }}>
          {filled} / {total} fields · {pct}%{savedAt ? " · saved ✓" : ""}
        </span>
        <button className="btn btn-sm" title="Fill blank fields from the pilot record" onClick={pullFromPilot}>Pull from pilot</button>
        <button className="btn btn-sm" onClick={copyMarkdown}>{copied ? "Copied ✓" : "Copy Markdown"}</button>
        <button className="btn btn-sm" onClick={onClose}>Close</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
      </div>}>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 12, lineHeight: 1.5 }}>
        Fill this in as the pilot runs, not after it ends — the detail you need fades fast once the engagement is over.
        Capture the baseline at kickoff, the results at day 90, and the quote while the goodwill is fresh.
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ height: 6, background: "var(--bg-sunken, #f4f1ea)", borderRadius: 999 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--green, #047857)", borderRadius: 999, transition: "width .25s" }} />
        </div>
      </div>
      {error && <div className="form-error" style={{ marginBottom: 8 }}>{error}</div>}
      {PILOT_CASE_STUDY_SECTIONS.map(sec => (
        <div key={sec.key} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600, marginBottom: 4 }}>{sec.title}</div>
          {sec.note && <div className="muted" style={{ fontSize: 12, lineHeight: 1.45, marginBottom: 10 }}>{sec.note}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
            {sec.fields.map(f => (
              <div className="form-field" key={f.key} style={f.type === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
                <label>{f.label}{f.help && <span className="muted" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> · {f.help}</span>}</label>
                {renderField(f)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Dialog>
  );
}

// ── Day 0-90 progress bar (per-row visual) ──────────────────────────
// Shows how far through the initial 90-day pilot window each engagement
// is. Days clamp to [0, 90] so the bar never overflows visually; the
// title attr surfaces the raw day count and the day-90 date on hover.
function PilotProgressBar({ startDate, day90Date }) {
  if (!startDate) return <span className="muted" style={{ fontSize: 11 }}>—</span>;
  const start = new Date(startDate);
  const today = new Date();
  const daysElapsed = Math.floor((today - start) / 86400000);
  const clamped = Math.max(0, Math.min(90, daysElapsed));
  const pct = clamped / 90;
  const complete = daysElapsed >= 90;
  const fill = complete ? "var(--green, #047857)" : "var(--accent, #047857)";
  const title = `${daysElapsed} days since start${day90Date ? ` · day 90: ${day90Date}` : ""}`;
  return (
    <div title={title} style={{ minWidth: 110 }}>
      <div style={{ height: 6, background: "var(--bg-sunken, #f4f1ea)", border: "1px solid var(--divider, #e7e2d5)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct * 100}%`, height: "100%", background: fill, transition: "width .25s" }} />
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
        {complete ? `${daysElapsed}d (past day 90)` : `${clamped} / 90d`}
      </div>
    </div>
  );
}

// ── Pilots list tab ─────────────────────────────────────────────────
function PilotsListTab({ navigate, onOpenGatelog }) {
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);
  const [checklistFor, setChecklistFor] = useState(null);
  const [caseStudyFor, setCaseStudyFor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");

  const pilots = (D.PILOTS || []).filter(p => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (accountFilter !== "all" && p.accountId !== accountFilter) return false;
    return true;
  });

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New pilot</button>
      </div>
      <div className="card">
        <FilterToolbar count={pilots.length} total={(D.PILOTS || []).length}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {PILOT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={accountFilter} onChange={e => setAccountFilter(e.target.value)}>
            <option value="all">All accounts</option>
            {(D.ACCOUNTS || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </FilterToolbar>
        {pilots.length === 0 ? (
          <div className="card-body muted">No pilots match. Click <strong>+ New pilot</strong> to create one.</div>
        ) : (
          <table className="tbl tbl-flush">
            <thead><tr>
              <th>Pilot</th>
              <th>Account</th>
              <th>Status</th>
              <th>Owner</th>
              <th>Start</th>
              <th>Progress</th>
              <th className="right">Pilot fee</th>
              <th>Checklist</th>
              <th></th>
            </tr></thead>
            <tbody>
              {pilots.map(p => {
                const acct = getAccount(p.accountId);
                const owner = getUser(p.ownerId);
                const { done, total } = checklistProgress(p.checklistState);
                const cs = caseStudyProgress(p.caseStudy);
                return (
                  <tr key={p.id} style={{ cursor: "default" }}>
                    <td><span className="tbl-link" onClick={() => setDetailsFor(p)}>{p.name}</span></td>
                    <td>{acct ? <span className="tbl-link" onClick={() => navigate("sales/accounts/" + acct.id)}>{acct.name}</span> : <span className="muted">—</span>}</td>
                    <td>
                      <Badge tone={pilotStatusTone(p.status)}>{pilotStatusLabel(p.status)}</Badge>
                      {p.paid ? <Badge tone="green">Paid</Badge> : <Badge tone="amber">Unpaid</Badge>}
                    </td>
                    <td>{owner ? <Avatar user={p.ownerId} size={20} /> : <span className="muted">—</span>}</td>
                    <td>{fmtDateShort(p.startDate)}</td>
                    <td><PilotProgressBar startDate={p.startDate} day90Date={p.day90Date} /></td>
                    <td className="right numeric">{fmtPounds(p.pilotFeePence)}</td>
                    <td>
                      <span className="muted" style={{ fontSize: 12 }}>{done}/{total}</span>
                    </td>
                    <td className="right" onClick={e => e.stopPropagation()}>
                      <button className="btn btn-sm btn-icon" title="View details" onClick={() => setDetailsFor(p)}>🔍</button>
                      <button className="btn btn-sm btn-icon" title="Edit pilot" onClick={() => setEditing(p)}>✎</button>
                      <button className="btn btn-sm btn-icon" title="Open onboarding checklist" onClick={() => setChecklistFor(p)}>📋</button>
                      <button className="btn btn-sm btn-icon" title={`Open case study${cs.filled ? ` · ${cs.filled}/${cs.total} filled` : ""}`} onClick={() => setCaseStudyFor(p)}>📝</button>
                      <button className="btn btn-sm btn-icon" title="Open gatelog for this pilot" onClick={() => onOpenGatelog && onOpenGatelog(p.id)}>🎯</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewPilotDialog onClose={() => setShowNew(false)} />}
      {editing && <EditPilotDialog pilot={editing} onClose={() => setEditing(null)} />}
      {detailsFor && <PilotDetailsDialog pilot={detailsFor} onClose={() => setDetailsFor(null)} />}
      {checklistFor && <ChecklistDialog pilot={checklistFor} onClose={() => setChecklistFor(null)} />}
      {caseStudyFor && <CaseStudyDialog pilot={caseStudyFor} onClose={() => setCaseStudyFor(null)} />}
    </>
  );
}

window.PilotsListTab = PilotsListTab;
